import type { QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { LEADS_KEY } from './leadCache'
import { dropLeadFromFocus, refreshLeadInLists } from '@/shared/api/leadLists'
import type { Lead, LeadRow } from '@/shared/types'
import { isDoNotContactRefusal } from './doNotContactRules'

/**
 * The company name for a lead id, read off whatever is already cached: the
 * single-lead query if it's loaded, else the first list row that matches.
 * The write that just failed or succeeded came from this lead's own cached
 * row, so the fallback label is only ever a last resort.
 */
export function leadCompany(qc: QueryClient, id: string): string {
  const single = qc.getQueryData<Lead>(['lead', id])
  if (single) return single.company
  for (const [, data] of qc.getQueriesData<{ leads: LeadRow[] }>({ queryKey: LEADS_KEY })) {
    const hit = data?.leads.find(l => l.id === id)
    if (hit) return hit.company
  }
  return 'den här leaden'
}

/** Sticky, like a failed write: something the seller meant to log was not logged. */
export function notifyDoNotContact(company: string): void {
  toast.error(
    `${company} är markerad Kontakta ej. Kontakten har bett att inte bli kontaktad, så det går inte att logga något på leaden.`,
    { duration: Infinity, closeButton: true },
  )
}

/**
 * The server refused a write with 409 "Kontakta ej": someone marked the lead
 * after this client loaded it. No retry, since a retry would be refused the
 * same way. The lead is refetched, so the card shows the notice and Focus
 * lets go of it.
 */
export function notifyDoNotContactRefusal(qc: QueryClient, leadId: string): void {
  notifyDoNotContact(leadCompany(qc, leadId))
  void refreshLeadInLists(qc, leadId).then(() => {
    if (qc.getQueryData<Lead>(['lead', leadId])?.doNotContact) dropLeadFromFocus(qc, leadId)
  })
}

/**
 * A lead write failed and the optimistic change was rolled back. Sticky
 * until dismissed, because a reverted edit that nothing says out loud looks
 * like it just worked.
 */
export function notifyWriteFailed(
  qc: QueryClient,
  leadId: string,
  action: string,
  retry: () => void,
  err?: unknown,
): void {
  if (isDoNotContactRefusal(err)) {
    notifyDoNotContactRefusal(qc, leadId)
    return
  }
  toast.error(`Kunde inte spara ${action} för ${leadCompany(qc, leadId)}.`, {
    duration: Infinity,
    closeButton: true,
    action: { label: 'Försök igen', onClick: retry },
  })
}

/**
 * A one-click field edit saved — the table's inline stage, booked and release
 * changes (YCRM-17). A 10s toast with Undo. A saved outcome shows nothing
 * (YCRM-96): the queue moving on is the confirmation.
 */
export function notifyFieldSaved(message: string, undo: () => void): void {
  toast.success(message, {
    duration: 10_000,
    action: { label: 'Ångra', onClick: undo },
  })
}
